import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { JobLogsDrawer } from '@/console/features/jobs/components/job-logs-drawer/job-logs-drawer';
import { TConversionStatusProps } from '@/console/features/projects/components/conversion-status/conversion-status.types';
import { useConversionStatus } from '@/console/features/projects/hooks/use-conversion-status/use-conversion-status';
import { RefreshIcon } from '@/shared/assets/icons/refresh-icon';
import { Button } from '@/shared/components/ui/button/button';
import { Chip } from '@/shared/components/ui/chip/chip';
import { Spinner } from '@/shared/components/ui/spinner/spinner';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { useEnumLabel } from '@/shared/hooks/use-enum-label/use-enum-label';
import { useVendor } from '@/shared/hooks/use-vendor/use-vendor';
import { FeatureEnum, JobStatusEnum } from '@/shared/types/types.generated';

export const ConversionStatus: React.FC<TConversionStatusProps> = ({
  project,
  versionId,
}) => {
  const { t } = useTranslation();
  const { isFeatureSupported } = useVendor();
  const isConversionSupported = isFeatureSupported({
    feature: FeatureEnum.ProjectConvert,
    ...project,
  });
  const {
    job,
    jobId,
    status,
    isLoading,
    errorMessage,
    triggerConversion,
    isCreateConversionLoading,
  } = useConversionStatus({
    versionId,
    projectId: project.projectId,
    disabled: !isConversionSupported,
  });
  const { getStatusColor } = useJobStatus();
  const { getEnumLabel } = useEnumLabel();

  const _triggerConversion = async () => {
    await triggerConversion(project.projectId, versionId);
  };

  const getChipText = () => {
    if (isLoading) {
      return t('common:loading');
    }

    if (status) {
      return getEnumLabel(status, 'pipelines:job_execution_status');
    }

    return t('pipelines:no_executions');
  };

  const canTriggerConversion = () => {
    const hasFailedStatus =
      status === JobStatusEnum.Failed ||
      status === JobStatusEnum.TimedOut ||
      status === JobStatusEnum.Skipped ||
      !status;

    return hasFailedStatus && !isCreateConversionLoading;
  };

  const getLabel = () => {
    if (isCreateConversionLoading) {
      return (
        <Stack direction="row" alignItems="center" gap="0.375rem">
          <Spinner size="xs" />
          <Typography variant="button" textTransform="capitalize">
            {getChipText()}
          </Typography>
        </Stack>
      );
    }

    return getChipText();
  };

  const getRetryConversionButton = () => {
    if (!canTriggerConversion()) {
      return null;
    }

    return (
      <Button
        size="extraSmall"
        variant="secondary"
        onClick={_triggerConversion}
        icon={<RefreshIcon />}
        tooltip={t('pipelines:trigger_conversion')}
      >
        {t('common:retry')}
      </Button>
    );
  };

  if (!isConversionSupported) {
    return '-';
  }

  return (
    <JobLogsDrawer
      jobId={jobId}
      job={job}
      trigger={
        <Chip
          tooltip={errorMessage}
          status={getStatusColor(status)}
          label={getLabel()}
        />
      }
      action={getRetryConversionButton()}
    />
  );
};
